import { useForm, FormProvider, useFieldArray, useWatch } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsRight, Info, Plus, Loader2 } from "lucide-react";
import RHFInput from "@/components/form/RHFInput";
import RHFSelect from "@/components/form/RHFSelect";
import RHFTextArea from "@/components/form/RHFTextArea";
import RHFDateInput from "@/components/form/RHFDateInput";
import ShiftSelector from "@/components/admin/job/ShiftSelector";
import CalendarTypeSelector from "@/components/admin/job/CalendarTypeSelector";
import { Button } from "@/components/ui/Button";
import InfoTip from "@/components/ui/InfoTip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/Accordion";
import RHFTagInput from "@/components/form/RHFTagInput";
import { FORM_INPUT_STYLE } from "@/components/form/fieldStyles";
import FieldConfigSelector from "@/components/admin/job/FieldConfigSelector";
import ConsentConfigSelector from "@/components/admin/job/ConsentConfigSelector";
import ApplicationQuestionsEditor from "@/components/admin/job/ApplicationQuestionsEditor";
import { EMPLOYEE_TYPE, JOB_PRIORITY } from "@/config/dropdownData";
import { zodResolver } from "@hookform/resolvers/zod";
import { jobFormSchemaFor } from "../validation.schema";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import { cn } from "@/lib/utils";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { StepIndicator } from "@/components/ui/StepIndicator";
import { currencyOptions } from "@/utils/currencyOptions";
import { JobService } from "@/services/jobService";
import { TenantSettingsService } from "@/services/tenantSettingsService";
import CandidateStatusService from "@/services/candidateStatusService";
import toast from "react-hot-toast";

// The server writes its own confirmation_text for the ConfirmationRecord
// (see job/service.py::_status_flow_change_confirmation_text) — this is
// just the checkbox label shown before that request goes out, so it should
// say the same thing: candidates still at initial get carried over, not
// left behind.
const statusFlowChangeText = (candidateCount) =>
  candidateCount > 0
    ? `Changing this job's candidate status flow changes which pipeline steps and transitions are available for every future applicant. ${candidateCount} candidate(s) currently in the initial stage will be moved to the new flow's initial status. I understand and wish to proceed.`
    : "Changing this job's candidate status flow changes which pipeline steps and transitions are available for every future applicant. I understand and wish to proceed.";

const STEP1_FIELDS = [
  "jobTitle", "deadline", "location", "employeeType",
  "shiftType", "predefinedShiftId", "shiftStartTime", "shiftEndTime", "shiftTimezone",
  "vacancyCount", "jobPriority", "experienceMin", "experienceMax",
  "skills", "overview", "responsibility",
  "companyWebsite", "linkedIn", "niceToHave", "certifications", "languagesRequired",

];

// Only the fields that are actually required by jobFormSchema. Drives the
// "Basic Information" step-indicator completion check. The wider STEP1_FIELDS
// list stays as-is because scroll-to-error / accordion-open logic must still
// react to validation errors on optional fields (bad URL in companyWebsite,
// overly long niceToHave text, etc.) — those just don't gate step completion.
const STEP1_REQUIRED_FIELDS = [
  "jobTitle", "deadline", "location",
  "vacancyCount", "experienceMin",
  "skills", "overview", "responsibility",
];

const stepConfig = [
  { key: "basic", label: "Basic Information" },
  { key: "config", label: "Field Configuration" },
];


// Main Component 

const JobForm = ({
  onSubmit,
  onClose,
  defaultValues,
  submitLabel = "Post Job",
  hideActions = false,
  submitRef,
  nextRef,
  onStep1CompleteChange,
  // Step 2's consent section pre-sets its switches from the org default after
  // its own fetch. Until that lands, submitting would create a job whose three
  // consent columns were never written — following the org default rather than
  // the answer the form was about to show. The buttons below wait for it, and
  // in controlled mode this reports the same readiness to whoever owns them.
  onConsentReadyChange,
  activeTab = 1,
  setActiveTab = () => { },
  isSubmitting = false,
  loadingLabel = "Submitting...",
  userOptions,
  // Optional read-only label rendered inside the "Assigned To" trigger when
  // the current assignedTo value doesn't match any option in userOptions —
  // used by the Edit flow to display a higher-role assignee's name without
  // showing them in the dropdown list itself. Add/AI flows don't pass it.
  assignedToFallbackLabel,
}) => {
  const [step, setStep] = useState(1);
  const isControlled = hideActions;
  const currentStep = isControlled ? activeTab : step;
  const [openAccordions, setOpenAccordions] = useState([]);
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const [isCurrencyUnlocked, setIsCurrencyUnlocked] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [isFlowUnlocked, setIsFlowUnlocked] = useState(false);
  const [flowOptions, setFlowOptions] = useState([]);
  const [consentReady, setConsentReady] = useState(false);
  const isEditMode = !!defaultValues?.id;
  const formRootRef = useRef(null);

  // The flow locks once any applicant has moved past the flow's initial
  // status — not simply "has any applicant." A job whose applicants are
  // all still sitting at initial can still change flows (they get carried
  // over to the new flow's own initial status). See
  // job/service.py::update_job_service. Cloning the job is the way to
  // reuse a different flow once it's actually locked.
  const flowLocked = isEditMode && defaultValues?.canChangeStatusFlow === false;
  const hasInitialStageApplicants =
    !flowLocked && Number(defaultValues?.candidateCount ?? 0) > 0;

  useEffect(() => {
    CandidateStatusService.listFlows()
      .then((res) => {
        const rawList = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
        setFlowOptions(
          rawList
            // A flow with no initial/hired/rejected status yet would only
            // fail at submit (FLOW_NOT_READY) — leave it out of the picker
            // rather than let it be picked and rejected. Same exception as
            // isActive: the job's own current flow always stays selectable,
            // even if it somehow later became inactive or incomplete.
            .filter(
              (f) =>
                (f?.isActive && f?.isReady) || f?.id === defaultValues?.statusFlowId
            )
            .map((f) => ({
              label: f?.isDefault ? `${f?.name} (Default)` : f?.name,
              value: f?.id,
            }))
        );

        // New jobs default to the tenant's Default flow, same as currency
        // prefills from the tenant's default above — pick it once flows
        // have loaded, if the form doesn't already have one.
        if (!isEditMode && !methods.getValues("statusFlowId")) {
          const defaultFlow = rawList.find((f) => f?.isDefault);
          if (defaultFlow?.id) {
            methods.setValue("statusFlowId", defaultFlow.id, { shouldValidate: true });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load status flows:", err);
        toast.error("Failed to load status flows. Please refresh and try again.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    TenantSettingsService.getDefaultCurrency()
      .then((result) => {
        const currencyCode = result.currency || "";

        // Prefill only for new jobs that don't already carry a currency
        // (e.g. one extracted from an AI-parsed JD) — edit mode keeps the
        // job's saved currency, gated behind its own confirmation flow.
        if (currencyCode && !isEditMode && !methods.getValues("currency")) {
          methods.setValue("currency", currencyCode, { shouldValidate: true });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEditMode) {
      JobService.fetchConfirmationText()
        .then((res) => {
          const text = res?.data?.confirmation_text || res?.text || res?.message || res || "I confirm I want to edit the currency.";
          setConfirmationText(typeof text === "string" ? text : String(text));
        })
        .catch((err) => console.error("Failed to fetch confirmation text:", err));
    }
  }, [isEditMode]);

  const defaultInterviewers = defaultValues?.interviewers?.length
    ? defaultValues.interviewers
    : [{ name: "", email: "" }];

  // The job's own stored deadline is exempt from the future-date rule — an
  // edit re-sends it untouched, and the server exempts the same value.
  const deadlineSchema = useMemo(
    () => jobFormSchemaFor(defaultValues?.deadline || null),
    [defaultValues?.deadline],
  );

  const methods = useForm({
    resolver: zodResolver(deadlineSchema),
    mode: "onChange",
    defaultValues: {
      ...defaultValues,
      vacancyCount: defaultValues?.vacancyCount?.toString() || "",
      experienceMin: defaultValues?.experienceMin?.toString() || "",
      experienceMax: defaultValues?.experienceMax?.toString() || "",
      skills: defaultValues?.skills || [],
      languagesRequired: defaultValues?.languagesRequired || [],
      interviewers: defaultInterviewers,
      mandatoryFields: defaultValues?.mandatoryFields || [],
      selectedConfigurable: defaultValues?.selectedConfigurable || [],
      selectedInterviewTypes: defaultValues?.selectedInterviewTypes || [],
      currency: defaultValues?.currency || "",
      currency_sign: defaultValues?.currency_sign || "",
      acknowledged: defaultValues.acknowledged ?? false,
      statusFlowId: defaultValues?.statusFlowId || "",
    },
  });

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
    reset,
  } = methods;

  const selectedCurrency = useWatch({
    control,
    name: "currency",
  });

  useEffect(() => {
    const selected = currencyOptions.find(
      (item) => item.value === selectedCurrency
    );

    if (!selectedCurrency) {
      // Currency cleared — keep the pair consistent so the backend's
      // both-or-neither rule never sees a lone currency_sign.
      methods.setValue("currency_sign", "");
      return;
    }

    // Some ISO codes have no symbol in currency-symbol-map (e.g. XAF).
    // Preserve the current sign instead of wiping it to "", which would
    // leave a currency code without a sign and trip the backend's atomic
    // currency-pair validation on submit.
    methods.setValue(
      "currency_sign",
      selected?.symbol || methods.getValues("currency_sign") || ""
    );
  }, [selectedCurrency, methods]);

  const { fields: interviewers, append: appendInterviewer, remove: removeInterviewer } =
    useFieldArray({ control, name: "interviewers" });

  const isLastInterviewerValid = () => true;

  // ── Step 1 error + completion state
  const step1HasErrors = STEP1_FIELDS.some((field) => {
    if (field === "interviewers") return false;
    return !!errors[field];
  });

  const isStep1Complete = STEP1_REQUIRED_FIELDS.every((field) => {
    const val = methods.getValues(field);

    if (Array.isArray(val)) return val.length > 0;

    return !!String(val ?? "").trim();
  });

  const stepsWithErrors = [
    !isStep1Complete,
    false,
  ];
  // ── Scroll helper

  // Walks up from `el` to the nearest ancestor that actually scrolls. Both
  // ManualJobPost and EditJobForm wrap JobForm in an `overflow-y-auto` div —
  // JobForm draws no scroll styling of its own — so this is how it finds that
  // ancestor without either caller having to hand it a ref.
  const findScrollParent = (el) => {
    let node = el?.parentElement;
    while (node) {
      const overflow = window.getComputedStyle(node).overflowY;
      if (overflow === "auto" || overflow === "scroll") return node;
      node = node.parentElement;
    }
    return null;
  };

  const scrollToFirstError = async (formErrors) => {
    // Walk STEP1_FIELDS first (so the scroll lands on the earliest visible
    // field in form order), then any *other* errored key the schema produced.
    // `currency` and `interviewers` are both required by jobFormSchema but
    // absent from STEP1_FIELDS — iterating STEP1_FIELDS alone meant an error
    // on either one scrolled nowhere and gave the user no clue why submit
    // did nothing.
    const errorKeys = Object.keys(formErrors ?? {});
    const ordered = [
      ...STEP1_FIELDS.filter((f) => errorKeys.includes(f)),
      ...errorKeys.filter((f) => !STEP1_FIELDS.includes(f)),
    ];

    for (const field of ordered) {
      if (!formErrors?.[field]) continue;

      const accordionKey = FIELD_TO_ACCORDION[field];
      if (accordionKey) {
        setOpenAccordions((prev) =>
          prev.includes(accordionKey) ? prev : [...prev, accordionKey]
        );
        await new Promise((r) => setTimeout(r, 300));
      }

      // ── Try [name] first, fall back to [data-field] for custom inputs like date pickers
      // Field arrays (interviewers) have no element matching the bare name, so
      // also try the first indexed row before giving up.
      const el =
        document.querySelector(`[data-field="${field}"]`) ??
        document.querySelector(`[name="${field}"]`) ??
        document.querySelector(`[name^="${field}."]`);

      // `continue`, not `return`: a field with no rendered element (e.g. a
      // field-array root) must not abort the walk — the next errored field may
      // well be scrollable, and returning here left the user staring at an
      // unchanged form.
      if (!el) continue;

      const scrollParent = findScrollParent(el);

      if (scrollParent) {
        const zoom = parseFloat(window.getComputedStyle(scrollParent).zoom) || 1;
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const offset = (elRect.top - parentRect.top) / zoom + scrollParent.scrollTop - 80;
        scrollParent.scrollTo({ top: offset, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      // Focus the real input inside the wrapper if we landed on a data-field div
      const focusTarget = el.matches("input, textarea, select")
        ? el
        : el.querySelector("input, textarea, select");
      focusTarget?.focus();

      return;
    }
  };

  // ── Navigation
  const handleNext = async () => {
    const valid = await trigger(STEP1_FIELDS);
    if (valid) {
      setStep(2);
      if (isControlled) setActiveTab(2);
      const scrollParent = findScrollParent(formRootRef.current);
      if (scrollParent) scrollParent.scrollTop = 0;
    } else {
      scrollToFirstError(methods.formState.errors);
    }
  };

  const handleBack = () => {
    setStep(1);
    if (isControlled) setActiveTab(1);
  };

  const onFormSubmit = (data) => {
    const cleaned = {
      ...data,
      currency: data.currency || methods.getValues("currency") || defaultValues?.currency || "",
      acknowledged: isCurrencyUnlocked,
      statusFlowAcknowledged: isFlowUnlocked,
      interviewers: data.interviewers.filter(
        (i) => i.name?.trim() || i.email?.trim()
      ),
      // A row the user opened but never touched is not a requirement; drop
      // it rather than sending blanks down the wire. A half-filled row never
      // reaches here — the schema refuses it.
      languagesRequired: (data.languagesRequired || []).filter(
        (row) => row.language?.trim() || row.proficiency?.trim()
      ),
    };
    onSubmit(cleaned);
  };
  // Called by react-hook-form when validation blocks the submit. This MUST
  // surface something for every possible error shape: previously it only
  // reacted to STEP1_FIELDS errors, so a failure on `currency` or
  // `interviewers` (both required by jobFormSchema, neither in STEP1_FIELDS)
  // silently swallowed the click — the "Post Job" button looked enabled and
  // simply did nothing, with no message, no scroll, and no step change.
  const onSubmitError = (formErrors) => {
    const errorKeys = Object.keys(formErrors ?? {});
    if (errorKeys.length === 0) return;

    const hasStep1Error = STEP1_FIELDS.some((field) => !!formErrors[field]);

    // Send the user back to step 1 when that's where the problem is.
    if (hasStep1Error && currentStep !== 1) {
      setStep(1);
      if (isControlled) setActiveTab(1);
    }

    // Always scroll/focus, regardless of which step owns the bad field.
    setShouldScrollToError(true);

    // And always say something out loud. `interviewers` and `currency` render
    // their messages in places the user may not have scrolled to, and a
    // field-array root error has no inline slot at all.
    const firstKey =
      STEP1_FIELDS.find((f) => errorKeys.includes(f)) ?? errorKeys[0];
    const rawMessage =
      formErrors[firstKey]?.message ??
      formErrors[firstKey]?.root?.message ??
      // Field arrays nest per-row errors; dig out the first real message.
      (Array.isArray(formErrors[firstKey])
        ? formErrors[firstKey]
            .filter(Boolean)
            .flatMap((row) => Object.values(row ?? {}))
            .find((e) => e?.message)?.message
        : undefined);

    toast.error(rawMessage || "Please fix the highlighted fields before posting.", {
      position: "top-right",
    });
  };

  // ── Stale closure fix for onSubmitError

  const onSubmitErrorRef = useRef(null);
  onSubmitErrorRef.current = onSubmitError;

  // ── Accordion map 

  const FIELD_TO_ACCORDION = {
    department: "Organization",
    division: "Organization",
    companyWebsite: "company-links",
    linkedIn: "company-links",
    niceToHave: "nice-to-have",
    certifications: "certifications",
    languagesRequired: "language",
  };

  // ── Effects 

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit(onFormSubmit, (...args) => onSubmitErrorRef.current?.(...args));
    }
  }, [submitRef, handleSubmit]);

  useEffect(() => {
    if (nextRef) nextRef.current = handleNext;
  }, [nextRef, handleNext]);

  useEffect(() => {
    onStep1CompleteChange?.(isStep1Complete);
  }, [isStep1Complete]);

  useEffect(() => {
    onConsentReadyChange?.(consentReady);
  }, [consentReady]);

  useEffect(() => {
    if (!shouldScrollToError) return;
    scrollToFirstError(errors);
    setShouldScrollToError(false);
  }, [currentStep, shouldScrollToError]);

  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return;
    const toOpen = new Set();
    Object.keys(errors).forEach((f) => { if (FIELD_TO_ACCORDION[f]) toOpen.add(FIELD_TO_ACCORDION[f]); });
    setOpenAccordions(Array.from(toOpen));
  }, [errors]);

  useEffect(() => {
    if (!defaultValues) return;
    const toOpen = new Set();
    Object.entries(FIELD_TO_ACCORDION).forEach(([field, key]) => {
      const value = defaultValues[field];
      const has = Array.isArray(value) ? value.length > 0 : typeof value === "string" ? value.trim() !== "" : !!value;
      if (has) toOpen.add(key);
    });
    if (toOpen.size > 0) setOpenAccordions((prev) => [...new Set([...prev, ...toOpen])]);
  }, [defaultValues]);

  useEffect(() => {
    if (!defaultValues) return;
    const { mandatoryFields, selectedConfigurable, ...rest } = defaultValues;
    reset({
      ...rest,
      vacancyCount: defaultValues?.vacancyCount?.toString() || "",
      experienceMin: defaultValues?.experienceMin?.toString() || "",
      experienceMax: defaultValues?.experienceMax?.toString() || "",
      skills: defaultValues?.skills || [],
      interviewers: defaultValues?.interviewers?.length ? defaultValues.interviewers : [{ name: "", email: "" }],
      mandatoryFields: mandatoryFields || [],
      currency: defaultValues?.currency || "",
      currency_sign: defaultValues?.currency_sign || "",
      selectedConfigurable: selectedConfigurable || [],
      assignedTo: defaultValues?.assignedTo ?? defaultValues?.assigned_to ?? "",
      acknowledged: defaultValues?.acknowledged ?? false,
    });
  }, [defaultValues, reset]);

  // ── Render

  return (
    <FormProvider {...methods}>
      <div ref={formRootRef} className={cn("p-1 max-w-2xl mx-auto", !hideActions && "pb-24 sm:pb-2")}>

        <StepIndicator
          stepsConfig={stepConfig}
          currentStep={currentStep}
          stepsWithErrors={stepsWithErrors}
          stepClick={(index) => {
            const stepNumber = index + 1;

            if (stepNumber === 1 && currentStep !== 1) {
              handleBack();
            }
          }}
        />
        {/* ── Step 1: Basic Information ─── */}
        {currentStep === 1 && (
          <>
            <h2 className="text-[16px] font-bold text-text-primary mb-5">Basic Information</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <RHFInput name="jobTitle" label="Job Title" placeholder="e.g. Backend Engineer" inputClass={FORM_INPUT_STYLE} required disabled={isSubmitting} />
              <div data-field="deadline">
                <RHFDateInput
                  disabled={isSubmitting}
                  name="deadline"
                  label="Application Due Date"
                  minDate={new Date()}
                  displayFormat="dd MMM yyyy"
                  required
                />
              </div>

              <div>
                <RHFInput name="location" label="Location" placeholder="e.g. Australia" inputClass={FORM_INPUT_STYLE} required disabled={isSubmitting} />
              </div>

              <div data-field="employeeType">
                <RHFSelect name="employeeType" label="Employee Type" placeholder="Select" options={EMPLOYEE_TYPE} disabled={isSubmitting} />
              </div>
              <RHFInput name="vacancyCount" label="Vacancy Count" placeholder="e.g. 3" type="number" min={1} max={10000} step={1} inputClass={FORM_INPUT_STYLE} disabled={isSubmitting} required />
              <div data-field="jobPriority">
                <RHFSelect name="jobPriority" label="Job Priority" placeholder="Select" options={JOB_PRIORITY} disabled={isSubmitting} />
              </div>
              <RHFInput name="experienceMin" label="Minimum Experience (years)" placeholder="e.g. 2" inputClass={FORM_INPUT_STYLE} disabled={isSubmitting} required />
              <RHFInput name="experienceMax" label="Maximum Experience (years)" placeholder="e.g. 5" inputClass={FORM_INPUT_STYLE} disabled={isSubmitting} />
              <div data-field="currency" className="relative group flex flex-col gap-1.5 focus-within:z-10">
                <RHFSelect
                  name="currency"
                  label="Currency"
                  placeholder="Search Currency"
                  options={currencyOptions}
                  disabled={isSubmitting || (isEditMode && !isCurrencyUnlocked)}
                  required
                  searchable
                />
                <div>
                  {isEditMode && confirmationText && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <RHFInput
                        name="acknowledged"
                        type="checkbox"
                        checked={isCurrencyUnlocked}
                        onChange={(e) => setIsCurrencyUnlocked(e.target.checked)}
                        className="mt-0.5 rounded border-border-primary text-button-secondary focus:ring-button-secondary bg-background-form"
                      />
                      <span className="text-xs text-text-secondary leading-tight">
                        {confirmationText}
                      </span>
                    </label>
                  )}
                </div>
              </div>
              <div data-field="statusFlowId" className="relative group flex flex-col gap-1.5 focus-within:z-10">
                <RHFSelect
                  name="statusFlowId"
                  label="Candidate Status Flow"
                  placeholder="Select a flow"
                  options={flowOptions}
                  disabled={isSubmitting || flowLocked || (isEditMode && !isFlowUnlocked)}
                  required
                  // The field's notes live behind the icon so they stop
                  // pushing the form apart; the acknowledgement checkbox
                  // below stays on screen, because it gates the change.
                  labelAddon={
                    <InfoTip label="About Candidate Status Flow">
                      {flowLocked ? (
                        <p>
                          A candidate on this job has moved past the initial stage —
                          the status flow can no longer be changed. Clone the job to
                          use a different flow.
                        </p>
                      ) : (
                        <p>
                          Only flows with an Initial, a Hired, and a Rejected status
                          show up here — isReusable is optional. Not seeing a flow
                          you expected? An admin can finish setting it up in
                          Candidate Status settings.
                        </p>
                      )}
                      {hasInitialStageApplicants && (
                        <p>
                          Every candidate on this job is still at the initial stage,
                          so the flow can still be changed — they'll be moved to the
                          new flow's initial status.
                        </p>
                      )}
                    </InfoTip>
                  }
                />
                {!flowLocked && isEditMode ? (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <RHFInput
                      name="statusFlowAcknowledged"
                      type="checkbox"
                      checked={isFlowUnlocked}
                      onChange={(e) => setIsFlowUnlocked(e.target.checked)}
                      className="mt-0.5 rounded border-border-primary text-button-secondary focus:ring-button-secondary bg-background-form"
                    />
                    <span className="text-xs text-text-secondary leading-tight">
                      {statusFlowChangeText(Number(defaultValues?.candidateCount ?? 0))}
                    </span>
                  </label>
                ) : null}
              </div>
              <PermissionGuard permission={PERMISSIONS.JOB_ASSIGN} isComponent>
                <RHFSelect
                  name={"assignedTo"}
                  label={"Assigned To"}
                  placeholder={"Select user"}
                  options={userOptions}
                  fallbackLabel={assignedToFallbackLabel}
                />
              </PermissionGuard>
              <div className="col-span-1 sm:col-span-2">
                <ShiftSelector
                  formMethods={methods}
                  disabled={isSubmitting}
                  legacyShiftTiming={defaultValues?.shiftTiming}
                />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <CalendarTypeSelector formMethods={methods} disabled={isSubmitting} />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm mb-1.5">Skills <span className="text-red-500 font-bold text-[16px] ml-1">*</span></label>
                <div data-field="skills">
                  <RHFTagInput disabled={isSubmitting} name="skills" placeholder="Type a skill and press enter" />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <RHFTextArea name="overview" disabled={isSubmitting} label="Role Overview" rows={5} placeholder="Describe the role..." inputClass={FORM_INPUT_STYLE} required />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <RHFTextArea name="responsibility" disabled={isSubmitting} label="Responsibilities" rows={5} placeholder="List key responsibilities..." inputClass={FORM_INPUT_STYLE} required />
              </div>

              {/* Interviewers */}
              <div className="col-span-1 sm:col-span-2">
                <h2 className="text-[16px] font-bold text-text-primary mb-2">Interviewers</h2>
                {interviewers.map((item, index) => (
                  <div key={item.id} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                    <RHFInput disabled={isSubmitting} name={`interviewers.${index}.name`} label="Name" placeholder="e.g. John Doe" inputClass={FORM_INPUT_STYLE} />
                    <div className="relative">
                      <RHFInput disabled={isSubmitting} name={`interviewers.${index}.email`} label="Email Address" placeholder="e.g. john@company.com" inputClass={FORM_INPUT_STYLE} />
                      {interviewers.length > 1 && (
                        <Button disabled={isSubmitting} type="button" onClick={() => removeInterviewer(index)} className="absolute -top-1 right-2 text-gray-400 hover:text-red-400 p-0">✕</Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  type="button"

                  onClick={() => appendInterviewer({ name: "", email: "" })}
                  disabled={isSubmitting || !isLastInterviewerValid()}
                  className={cn(
                    "mt-2 !px-4 py-2 text-sm rounded-lg border border-border-primary transition flex items-center gap-2",
                    !isLastInterviewerValid() ? "opacity-50 cursor-not-allowed" : "text-text-base bg-button-secondary border-none",
                  )}
                >
                  <Plus size={16} /> Add Interviewer
                </Button>
              </div>

              {/* Optional accordions */}
              <div className="col-span-1 sm:col-span-2">
                <Accordion value={openAccordions} onValueChange={setOpenAccordions} multiple>
                  {/* <AccordionItem value="Organization">
                    <AccordionTrigger>Organization</AccordionTrigger>
                    <AccordionContent className="pb-3 space-y-3">
                      <RHFInput name="department" label="Department" placeholder="e.g. Engineering" inputClass={FORM_INPUT_STYLE} />
                      <RHFInput name="division" label="Division" placeholder="e.g. Product" inputClass={FORM_INPUT_STYLE} />
                    </AccordionContent>
                  </AccordionItem> */}

                  <AccordionItem value="company-links">
                    <AccordionTrigger>Company Links</AccordionTrigger>
                    <AccordionContent className="pb-3 space-y-3">
                      <RHFInput disabled={isSubmitting} name="companyWebsite" label="Website" prefix="https://" placeholder="company.com" inputClass={FORM_INPUT_STYLE} />
                      <RHFInput disabled={isSubmitting} name="linkedIn" label="LinkedIn" placeholder="https://linkedin.com/company/..." inputClass={FORM_INPUT_STYLE} />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="nice-to-have">
                    <AccordionTrigger>Nice to Have</AccordionTrigger>
                    <AccordionContent>
                      <RHFTextArea disabled={isSubmitting} name="niceToHave" rows={5} placeholder="e.g. AWS Certified..." inputClass={FORM_INPUT_STYLE} />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="certifications">
                    <AccordionTrigger>Certifications</AccordionTrigger>
                    <AccordionContent>
                      <RHFTextArea disabled={isSubmitting} name="certifications" rows={5} placeholder="e.g. PMP, AWS..." inputClass={FORM_INPUT_STYLE} />
                    </AccordionContent>
                  </AccordionItem>

                   <AccordionItem value="language">
                     <AccordionTrigger>
                       <span className="flex items-center gap-1">Language &amp; Proficiency</span>
                     </AccordionTrigger>
                     <AccordionContent>
                       {/* Says the consequence before the recruiter acts, because
                           the Field Configuration step two clicks away is where
                           it lands and they cannot see it from here. Each half
                           separately, because that is the rule: a job asking for
                           a language with no level must not demand one. */}
                       <p className="mb-2 flex items-start gap-1.5 text-[12px] text-text-secondary">
                         <Info size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                         A language you add becomes mandatory for candidates applying to this job, and a proficiency too if you add one. Field Configuration updates to match, and removing them undoes it.
                       </p>
                        <LanguageProficiencyRows
                          name="languagesRequired"
                          disabled={isSubmitting}
                          emptyText="No languages required for this job. Add one if the role needs a specific language."
                        />
                     </AccordionContent>
                   </AccordionItem>

                </Accordion>
              </div>
            </div>
          </>
        )}

        {/* ── Step 2: Field Configuration ─── */}
        {
          currentStep === 2 && (
            <div className="flex flex-col gap-8">
              <FieldConfigSelector disabled={isSubmitting} formMethods={methods} jobId={defaultValues?.id} />
              <ConsentConfigSelector
                disabled={isSubmitting}
                formMethods={methods}
                inheritedFlags={defaultValues?.emailConsentInherited}
                onReadyChange={setConsentReady}
              />
              {/* Manual screening questions — rendered in the same "Configure
                  Job" step for every flow (manual create, AI Review & Update,
                  edit, clone). Part of the Job feature — no module gate. */}
              <ApplicationQuestionsEditor disabled={isSubmitting} />
            </div>
          )
        }

        {/* ── Actions (inline — no inner component to avoid remount/double-click bug) ─── */}
        {!hideActions && (
          <>
            {/* Desktop */}
            <div className="hidden sm:flex justify-end gap-3 mt-6">
              {currentStep === 1 ? (
                <>
                  <Button className="bg-button-tertiary" variant="outline" onClick={onClose}>Cancel</Button>
                  <Button
                    className="bg-button-secondary flex gap-2 items-center text-text-base"
                    type="button"
                    onClick={handleNext}
                  >
                    <span>Next</span> <ChevronRight size={16} />
                  </Button>
                </>
              ) : (
                <>
                  <Button className="bg-button-tertiary flex gap-2 items-center" variant="outline" type="button" onClick={handleBack} > <ChevronLeft size={16} /> <span>Back</span> </Button>
                  <Button
                    className="bg-button-secondary text-text-base flex items-center gap-2"
                    onClick={handleSubmit(onFormSubmit, (...args) => onSubmitErrorRef.current?.(...args))}
                    disabled={isSubmitting || !consentReady}
                  >
                    {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                    {isSubmitting ? loadingLabel : submitLabel}
                  </Button>
                </>
              )}
            </div>

            {/* Mobile fixed bar */}
            <div className="fixed bottom-0 left-0 right-0 z-[100] flex sm:hidden gap-3 px-4 py-4 bg-background-form border-t border-border-primary">
              {currentStep === 1 ? (
                <>
                  <Button className="bg-button-tertiary flex-1" variant="outline" onClick={onClose}>Cancel</Button>
                  <Button
                    className="bg-button-secondary flex gap-2 items-center text-text-base flex-1"
                    type="button"
                    onClick={handleNext}
                  >
                    <span>Next</span> <ChevronRight size={16} />
                  </Button>
                </>
              ) : (
                <>
                  <Button className="bg-button-tertiary flex-1 flex gap-2 items-center" variant="outline" type="button" onClick={handleBack}><ChevronLeft size={16} /> <span>Back</span></Button>
                  <Button className="bg-button-secondary text-text-base flex-1 flex items-center gap-2" onClick={handleSubmit(onFormSubmit, (...args) => onSubmitErrorRef.current?.(...args))} disabled={isSubmitting || !consentReady}>
                    {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                    {isSubmitting ? loadingLabel : submitLabel}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </FormProvider>
  );
};

export default JobForm;